<template>
  <!-- ----------------- -->
  <!-- TOP ROW (HEADER)  -->
  <!-- ----------------- -->
  <div class="mt-3">
    <div class="mt-3">
      <div class="row mb-0">
        <p class="bb-sheet-title">{{ spell_type }} Spells</p>
      </div>
    </div>
    <!-- ------------------------- -->
    <!-- OVERLAY: spell side panel *** NO UPDATES - JUST ADD/DELETE *** -->
    <!-- ------------------------- -->
    <SpellSidePanel
      v-if="isSidePanelOpen"
      :spell="character_spell"
      :spell_type="panelSpellType"
      :character="character"
      @add-character-spell="$emit('add-character-spell', $event)"
      @update-character-spell="$emit('update-character-spell', $event)"
      @delete-character-spell="$emit('delete-character-spell', $event)"
      @close-spell-panel="handleSpellPanelClose"
    />
    <!-- ------------------------ -->
    <!-- NOTIFICATIONS            -->
    <!-- ------------------------ -->
    <notifications
      width="500"
      group="bb-confirm"
      :speed="100"
      :duration="1000"
      position="top right"
      classes="vue-notification"
    />
    <!-- ------------------ -->
    <!--     spell table     -->
    <!-- ------------------ -->
    <div v-if="spells && character">
      <TableSpells
        ref="TableSpells"
        :headings="headings"
        :spell_type="spell_type"
        :spells="spells"
        :character="character"
        @update-spell-qty="$emit('update-character-spell', $event)"
        @show-character-spell="handleShowSpellPanel"
        @delete-character-spell="$emit('delete-character-spell', $event)"
      />
    </div>
  </div>
  <!-- <div v-if="!loadReady" class="bb-loader"></div> -->
  <!-- </div> -->
</template>

<script>
//========================================================
// scripts
//---------
import { inject } from 'vue'
import { mapGetters } from 'vuex'
import hasTempId from '@/helpers/hasTempId.js'

import SpellSidePanel from '../components/SpellSidePanel.vue'
import TableSpells from './TableSpells.vue'

export default {
  name: 'CharacterSheetSpells',
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // components
  //------------
  components: {
    TableSpells,
    SpellSidePanel,
  },
  //========================================================
  // props
  //-------
  props: {
    character: {
      type: Object,
      required: true,
    },
    spell_type: {
      type: String,
      required: true,
    },
    errorFields: {
      type: Array,
      required: true,
    },
    toggleDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    active_tab: {
      type: String,
      required: false,
      default: '',
    },
  },
  //========================================================
  // data
  //------
  data() {
    return {
      endpoint: '',
      local_slug: '',

      deleteInd: false,
      loadReady: false,
      initialLoad: true,
      isSidePanelOpen: false,

      character_spell: null,

      spells: [],
      errors: [],
      //
      name: 'SpellList',
      // headings: [],
      //
      headings: [
        // -------------------------------------------------------------------
        // basic details
        {
          id: 0,
          label: '#',
          field: 'rowNumber',
          filterable: false,
          sortable: true,
          tdClass: 'text-center',
          width: '4em',
        },
        {
          id: 5,
          label: 'Spell',
          field: 'name',
          width: '14em',
          filterable: false,
          sortable: true,
          formatFn: (value, row) => {
            return row.display_name || row.name
          },
        },
        {
          id: 5.5,
          label: 'Active Spell',
          field: 'active_spell_image',
          width: '8em',
          filterable: false,
          sortable: true,
          tdClass: 'text-center',
          formatFn: (value, row) => {
            return row.active_spell
          },
        },
        {
          id: 6,
          label: 'Type',
          field: 'type',
          filterable: false,
          sortable: true,
          tdClass: 'text-center',
        },
        {
          id: 7,
          label: 'Source',
          field: 'source',
          width: '18em',
          filterable: false,
          sortable: true,
          tdClass: 'text-left',
          // Enrich source label using available metadata so that
          // item spells can show the item name and rune spells can
          // show the casting rune name (and item, if mounted).
          formatFn: (value, row) => {
            const base = value || row.source

            // Item-granted spells: "Item: <item name>" when available
            if (base === 'Item') {
              const itemLabel = row.source_name || row.item_name
              return itemLabel ? `${itemLabel}` : base
            }

            if (base === 'Rune') {
              const runeLabel =
                row.rune_display_name ||
                row.source_name ||
                row.casting_rune_name
              const itemLabel = row.item_name

              // If we have both rune and item, show both
              if (runeLabel && itemLabel) {
                return `${runeLabel} (${itemLabel})`
              }

              // Fallbacks if only one is available
              if (runeLabel) return runeLabel
              if (itemLabel) return `Rune (on ${itemLabel})`

              return base
            }

            // Character-learned spells: show as "Known"
            if (base === 'Character') {
              return 'Known'
            }

            // Learned spells or anything else: fall back to base label
            return base
          },
        },
        {
          id: 7,
          label: 'Type',
          field: 'type',
          tdClass: 'text-center',
          filterable: false,
          hidden: true,
        },
        {
          id: 10,
          label: 'Description',
          field: 'short_description',
          filterable: false,
          sortable: false,
          hidden: false,
        },
        // -------------------------------------------------------------------
        // flags
        {
          id: 15,
          label: '',
          field: 'fill0',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 35,
          label: 'Progressive',
          field: 'progressive_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 20,
          label: 'Triggered',
          field: 'triggered_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        // -------------------------------------------------------------------
        // basic details
        {
          id: 40,
          label: '',
          field: 'fill1',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 45,
          label: 'Mag',
          field: 'mag',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 50,
          label: 'Resist',
          field: 'resist',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          formatFn: (value) => {
            return value === 'n/a' ? '' : value
          },
        },
        {
          id: 55,
          label: 'Area',
          field: 'area',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          formatFn: (value) => {
            return value === 'n/a' ? '' : value
          },
        },
        {
          id: 65,
          label: 'Range',
          field: 'range',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 55,
          label: 'Duration',
          field: 'duration',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 65,
          label: 'Casting Time',
          field: 'casting_time',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        // -------------------------------------------------------------------
        // hidden additional details
        {
          id: 103,
          label: '',
          field: 'fill4',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 105,
          label: 'Id',
          field: 'id',
          type: 'number',
          tdClass: 'text-nowrap',
          hidden: true,
        },
        {
          id: 110,
          label: 'Static Id',
          field: 'static_id',
          type: 'number',
          tdClass: 'text-nowrap',
          hidden: true,
        },
        // character spell flags
        {
          id: 112,
          label: 'Progressive Flag',
          field: 'progressive',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 115,
          label: 'Triggered Flag',
          field: 'triggered',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
      ],
    }
  },
  //========================================================
  // watch
  //-------
  watch: {
    toggleDetails(newVal) {
      // toggleDetails() {
      this.toggleDetailsHandler(newVal)
    },
    //---------------------------------------------------------------------
    // watch character so adding/removing spell updates table immediately
    //---------------------------------------------------------------------
    character: {
      handler(newChar) {
        console.log('=====================================')
        console.log('WATCH CHARACTER SPELLS....')
        if (!newChar) return
        // reload filtered/sorted spell for the new/updated character
        this.loadCharacterSpellsByType(newChar)
      },
      deep: true,
      immediate: true,
    },
  },
  //========================================================
  // computed
  //----------
  computed: {
    //--------
    // mapped
    //--------
    ...mapGetters('characters', {
      getStaticSpellById: 'getStaticSpellById',
    }),
    // Type to pass into the side panel / SpellCard.
    // For existing spells opened from the "All" tab, prefer the spell's own type
    // so the panel behaves as if you were on the type-specific tab.
    panelSpellType() {
      return this.character_spell && this.character_spell.type
        ? this.character_spell.type
        : this.spell_type
    },
    api_request() {
      return window.bbAPIRequest(this.endpoint, this.local_slug, this.deleteInd)
    },
    isInvertedSidePanelOpen() {
      return !this.isSidePanelOpen
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    normaliseComparableSpellValue(value) {
      if (value === undefined || value === null || value === '') {
        return null
      }

      return String(value).trim()
    },
    getSpellSourceLabel(spell) {
      return String(spell?.source ?? '')
        .trim()
        .toLowerCase()
    },
    getSpellItemLabel(spell) {
      return String(spell?.source_name ?? spell?.item_name ?? '').trim()
    },
    getSpellRuneLabel(spell) {
      return String(
        spell?.rune_display_name ??
          spell?.source_name ??
          spell?.casting_rune_name ??
          '',
      ).trim()
    },
    isRuneSpellActive(spell) {
      if (!spell?.character_rune_id) {
        return false
      }

      const targetRuneId = String(spell.character_rune_id)
      const runes = Array.isArray(this.character?.runes)
        ? this.character.runes
        : []

      return runes.some(
        (rune) =>
          String(rune?.id ?? '') === targetRuneId && rune?.integrated === true,
      )
    },
    isSorcerySpellActive(spell) {
      return Boolean(spell?.casting_skill || spell?.casting_skill_id)
    },
    getSpellActiveState(spell) {
      const sourceLabel = this.getSpellSourceLabel(spell)
      const typeLabel = String(spell?.type ?? '')
        .trim()
        .toLowerCase()

      if (sourceLabel === 'rune') {
        return this.isRuneSpellActive(spell)
      }

      if (typeLabel === 'sorcery') {
        return this.isSorcerySpellActive(spell)
      }

      if (typeLabel === 'divine') {
        return true
      }

      return true
    },
    getSpellStaticId(spell) {
      return this.normaliseComparableSpellValue(
        spell?.spell ?? spell?.static_id ?? null,
      )
    },
    isMatchingTempPersistedSpell(tempSpell, persistedSpell) {
      if (
        !tempSpell ||
        !persistedSpell ||
        !hasTempId(tempSpell?.id) ||
        hasTempId(persistedSpell?.id)
      ) {
        return false
      }

      const persistedHasStableId = Boolean(
        persistedSpell?.character_spell_id ||
          persistedSpell?.character_item_spell_id ||
          persistedSpell?.character_rune_spell_id ||
          (!hasTempId(persistedSpell?.id) && persistedSpell?.id),
      )

      if (!persistedHasStableId) {
        return false
      }

      if (
        this.getSpellSourceLabel(tempSpell) !==
        this.getSpellSourceLabel(persistedSpell)
      ) {
        return false
      }

      if (
        this.getSpellStaticId(tempSpell) !==
        this.getSpellStaticId(persistedSpell)
      ) {
        return false
      }

      if (
        this.normaliseComparableSpellValue(tempSpell?.mag) !==
        this.normaliseComparableSpellValue(persistedSpell?.mag)
      ) {
        return false
      }

      if (
        this.normaliseComparableSpellValue(tempSpell?.name) !==
        this.normaliseComparableSpellValue(persistedSpell?.name)
      ) {
        return false
      }

      if (
        this.getSpellSourceLabel(tempSpell) === 'rune' &&
        this.getSpellRuneLabel(tempSpell) !==
          this.getSpellRuneLabel(persistedSpell)
      ) {
        return false
      }

      if (
        ['rune', 'item'].includes(this.getSpellSourceLabel(tempSpell)) &&
        this.getSpellItemLabel(tempSpell) !==
          this.getSpellItemLabel(persistedSpell)
      ) {
        return false
      }

      return true
    },
    collapseTempPersistedSpellDisplayDuplicates(spells = []) {
      if (!Array.isArray(spells) || spells.length < 2) {
        return Array.isArray(spells) ? spells : []
      }

      return spells.filter((spell, index, source) => {
        if (!hasTempId(spell?.id)) {
          return true
        }

        return !source.some((candidate, candidateIndex) => {
          return (
            candidateIndex !== index &&
            this.isMatchingTempPersistedSpell(spell, candidate)
          )
        })
      })
    },
    //========================================================
    // load character spell by type
    //-------------------------------
    loadCharacterSpellsByType(character) {
      console.log('-------------------------------')
      console.log('IN loadCharacterSpellsByType spell_type = ', this.spell_type)
      // console.log(' this.character.spell = ', window.$deepCopy(this.character.spell))
      // IMPORTANT: do not mutate character.spell here, or we create
      // a reactive feedback loop. Work on shallow copies instead.

      const allSpells = Array.isArray(character && character.spells)
        ? character.spells
        : []

      const sourceSpells = this.collapseTempPersistedSpellDisplayDuplicates(
        this.spell_type === 'All'
          ? allSpells
          : allSpells.filter((i) => i && i.type == this.spell_type),
      )

      this.spells = sourceSpells
        .map((i) => {
          const spell = { ...i }
          // console.log('==================================')
          // console.log('PROCESSING SPELL = ', spell.name)
          // console.log(' SPELL = ', spell)
          //-----------------------
          // belt and braces check
          //-----------------------
          if (spell) {
            // console.log('GETTING STATIC ITEM FOR: ', spell.name)
            //------------------
            // get static spell
            //------------------
            const getStaticSpellByName =
              this.$store.getters['characters/getStaticSpellByName']
            const static_spell =
              typeof getStaticSpellByName === 'function'
                ? getStaticSpellByName(spell.name)
                : null
            // console.log('FOUND STATIC ITEM = ', static_spell)
            //----------------------
            // get static spell data
            //----------------------
            if (static_spell) {
              //-------------------------------------------------------------
              // copy static spell data

              spell.progressive = static_spell.progressive
              spell.triggered = static_spell.triggered
              spell.static_id = static_spell.id
              spell.type = static_spell.type
              spell.resist = static_spell.resist
              spell.area = static_spell.area
              spell.range = static_spell.range
              spell.duration = static_spell.duration
              spell.casting_time = static_spell.casting_time
              // spell.cult1 = static_spell.cult1
              // spell.cult2 = static_spell.cult2
              // spell.description = static_spell.description
              spell.short_description = static_spell.short_description
            }

            spell.active_spell = this.getSpellActiveState(spell)
          } else {
            console.error('NO STATIC SPELL FOR SPELL = ', i)
          }
          return spell
        })
        .sort((a, b) => {
          return a.name.localeCompare(b.name)
        })
      console.log('PPPPPP SPELLS = ', window.$deepCopy(this.spells))
    },
    //========================================================
    // handle show spell panel
    //-------------------------
    handleShowSpellPanel(character_spell) {
      console.log('IN handleShowSpellPanel')
      console.log('spell_type = ', this.spell_type)
      this.character_spell = character_spell
      this.isSidePanelOpen = this.isInvertedSidePanelOpen
    },
    //========================================================
    // handle add row
    //----------------
    handleAddRow() {
      console.log('IN handleAddRow')
      // this.character_spell = character_spell
      // this.isSidePanelOpen = this.isInvertedSidePanelOpen
    },
    //========================================================
    // handle scroll table
    //----------------------
    handleScrollTable(dir) {
      console.log('IN SheetSpells handleScrollTable dir = ', dir)
      // call method on TableSpells via ref
      this.$refs.TableSpells.handleScrollTable(dir)
    },
    //========================================================
    // forwardEmitOpenAdjs
    //---------------------
    forwardEmitOpenAdjs(data) {
      this.$emit('adjust-character-spell', data)
    },
    //========================================================
    // handle spell panel close
    //-------------------------
    handleSpellPanelClose() {
      console.log('IN handleSpellPanelClose...')
      // this.SetView('spell')
      this.isSidePanelOpen = this.isInvertedSidePanelOpen
    },
  },
  //========================================================
  // mounted
  //---------
  mounted() {
    if (this.spell_type !== 'All') {
      this.headings = this.headings.filter((h) => h.field !== 'type')
    }
  },
}
</script>
